import React from 'react';
import { BarChart, Trash2, Layers, Droplets } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов краски
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты расхода краски для сравнения.
          Сохраните несколько вариантов расчета для разных типов краски или поверхностей.
        </p>
      </div>
    );
  }

  // Форматирование даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {pluralize(savedCalculations.length, 'вариант', 'варианта', 'вариантов')}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calc) => (
          <div 
            key={calc.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calc.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calc.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calc.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Площадь:</span>
                <span className="font-medium">
                  {formatNumber(calc.results.totalArea || calc.formData.surfaceArea, 1)} м²
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Тип краски:</span>
                <span>
                  {calc.results.paintType || getTypeNameById(calc.formData.paintType)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Количество слоев:</span>
                <span>
                  {calc.formData.coatsNumber}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Расход краски:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatNumber(calc.results.exactPaintVolume || 0, 2)} л
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                {formatDate(calc.date)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {savedCalculations.length >= 2 && (
        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <Droplets className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-2">
                Сравнение расходов
              </h4>
              <div className="space-y-1">
                {savedCalculations.sort((a, b) => 
                  (a.results.exactPaintVolume || 0) - (b.results.exactPaintVolume || 0)
                ).map((calc, index) => (
                  <div key={calc.id} className="flex justify-between text-xs">
                    <span className="text-blue-600 dark:text-blue-400">
                      {index + 1}. {truncateName(calc.name, 20)}:
                    </span>
                    <span className="font-medium text-blue-700 dark:text-blue-300">
                      {formatNumber(calc.results.exactPaintVolume || 0, 2)} л
                    </span>
                  </div>
                ))}
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                Разница между max и min: {formatNumber(
                  Math.max(...savedCalculations.map(c => c.results.exactPaintVolume || 0)) - 
                  Math.min(...savedCalculations.map(c => c.results.exactPaintVolume || 0)), 
                  2
                )} л
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Функция для получения названия типа краски по ID
const getTypeNameById = (id) => {
  const types = {
    'water': 'Водоэмульсионная',
    'acrylic': 'Акриловая',
    'latex': 'Латексная',
    'silicone': 'Силиконовая',
    'alkyd': 'Алкидная',
    'oil': 'Масляная',
    'chalk': 'Меловая',
    'mineral': 'Минеральная',
    'silicate': 'Силикатная'
  };
  return types[id] || id;
};

// Функция для сокращения длинных названий
const truncateName = (name, maxLength) => {
  if (name.length <= maxLength) return name;
  return name.slice(0, maxLength) + '...';
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default ComparisonSidebar;